import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import {
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import {Store} from '@ngxs/store';
import {cloneDeep, find as _find, get as _get, set as _set} from 'lodash';
import {
  ColorOption,
  IProvince,
  IState,
  LaunchDarklyService,
  PostingDataResponseModel,
  StatesProvincesService,
  VehicleInformation,
  VehicleOption,
  VehicleTrimOption,
  VehicleType,
  InventoryService,
  VehicleOptions,
  InspectionOptionResponseModel,
  ChromeServiceInputModel,
  ConfigurationState,
  EditPostingState,
  SetChromeDataInput,
  UpdatePosting,
  DescriptionFormPostingNote,
  EditPostingFormNames,
  NewPostingInspectionOption,
  PostingTypes,
  EditPostingService
} from 'remarketing-angular-library';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import {catchError, filter, take, takeUntil} from 'rxjs/operators';
import {
  descriptionFormDropdownCustomValidator,
  descriptionFormOdometerCustomValidator
} from './description-form.validator';

import {PostingFormDirective} from '../posting-form/posting-form.directive';
import {of} from 'rxjs';
import {ErrorModalComponent} from '@ally/remarketing/shared/ui';
import { PostingFormTrackingService } from '../../services/posting-form-tracking.service';
import { ModalComponent, SpinnerComponent } from 'remarketing-ui-components-lib';
import { PostingDataHelperService } from '../../../../../vehicles/services/posting-data-helper/posting-data-helper.service';
import {PostingAnalyticsHelperService} from '../../services/posting-analytics-helper.service';
import { EditPostingUtilsService } from '../../services/edit-posting-utils.service';
import { NgxMaskModule } from 'ngx-mask';
import { CommonModule } from '@angular/common';

@Component({
    selector: 'app-description-form',
    templateUrl: './description-form.component.html',
    styleUrls: ['./description-form.component.scss'],
    imports: [
        TranslocoDirective,
        ReactiveFormsModule,
        SpinnerComponent,
        NgxMaskModule,
        ModalComponent,
        ErrorModalComponent,
        CommonModule
    ]
})
export class DescriptionFormComponent extends PostingFormDirective implements OnInit {
  postingNoteParam: string;
  @ViewChild(ErrorModalComponent) errorModal: ErrorModalComponent;
  @Output('vehicleHeaderHelper') vehicleHeaderHelper: EventEmitter<any> = new EventEmitter();
  @ViewChild(ModalComponent) trimChangeModal: ModalComponent;
  @HostListener('document:keydown.escape', ['$event']) onKeydownHandler(event: KeyboardEvent) {
    this.cancel();
  }
  @Input() postingTypeString: string;
  descriptionForm: UntypedFormGroup;
  postingData: PostingDataResponseModel;
  originalPostingData: PostingDataResponseModel;
  vehicleTrimOption: VehicleTrimOption[];
  vehicleTypes: VehicleType[];
  exteriorColorOptions: ColorOption[];
  interiorColorOptions: ColorOption[];
  interiorTypes: VehicleOption[];
  trimPlaceholder: string;
  defaultOdometer = 0;
  isRepostBefore60Days: boolean;
  fetchVinDecodeInProgress = false;
  vinDecodeErrorMessage: string;
  postingType: PostingTypes;
  textSelect: string;
  chromeResponseInProgress = false;
  chromeOptions: VehicleOptions;
  errorHeaderKey: string;
  errorMessageKey: string;
  chromeDataInput: ChromeServiceInputModel = {
    manufacturerModelCode: undefined,
    vehicleTrimName: undefined,
    manufacturerOptionCodes: undefined,
    equipmentDescriptions: undefined,
    exteriorColor: undefined,
    interiorColor: undefined
  };

  statesProvincesList: IState[] | IProvince[] = [];

  descriptionFormTranslations = {
    color: '',
    labelExterior: '',
    labelInterior: '',
    labelExteriorInterior: '',
    labelOdometer: '',
    labelTitleStateOrProvince: ''
  };

  private readonly vehicleInfo = 'inventoryItem.vehicleInformation';

  public trackFormChanges(inputIdentifier?: string) {
    _set(_find(this.vehicleTrimOption, { selected: true }), 'selected', false);
    _set(_find(this.vehicleTrimOption, { vehicleTrimName: this.descriptionForm.get('trim').value }), 'selected', true);

    _set(_find(this.vehicleTypes, { selected: true }), 'selected', false);
    _set(_find(this.vehicleTypes, { typeCode: this.descriptionForm.get('vehicleType').value }), 'selected', true);
    // clear buy now in valuation if buyNowAllowed is false
    if (this.showBuyNowNotAllowedError) {
      _set(this.postingData, 'inventoryItem.valuation.buyNowPrice', undefined);
    }

    _set(_find(this.exteriorColorOptions, { installed: true }), 'installed', false);
    _set(
      _find(this.exteriorColorOptions, { colorCode: this.descriptionForm.get('exteriorColor').value }),
      'installed',
      true
    );

    _set(_find(this.interiorColorOptions, { installed: true }), 'installed', false);
    _set(
      _find(this.interiorColorOptions, { colorCode: this.descriptionForm.get('interiorColor').value }),
      'installed',
      true
    );

    _set(_find(this.interiorTypes, { installed: true }), 'installed', false);
    _set(_find(this.interiorTypes, { optionCode: this.descriptionForm.get('interiorType').value }), 'installed', true);

    _set(this.postingData, 'inventoryItem.titleState.stateCode', this.descriptionForm.get('titleState').value);
    _set(this.postingData, `${this.vehicleInfo}.odometer`, this.descriptionForm.get('odometerMiles').value);
    _set(
      this.postingData,
      'inventoryItem.postingInformation.stockNumber',
      this.descriptionForm.get('stockOrAccount').value
    );

    const vehicleUse = this.descriptionForm.get('vehicleUse').value;
    const isRecreationalSelected = _get(this.postingData, `${this.vehicleInfo}.recreationalVehicle`);

    if ((vehicleUse === 'Neither' || vehicleUse === 'Commercial') && isRecreationalSelected) {
      this.fetchVinDecodeInProgress = true;
      const vin = this.postingData.inventoryItem.vehicleInformation.vin;
      this.fetchVinDecodedData(vin);
    }
    _set(this.postingData, `${this.vehicleInfo}.commercialVehicle`, vehicleUse === 'Commercial');
    _set(this.postingData, `${this.vehicleInfo}.recreationalVehicle`, vehicleUse === 'Recreational');
    this.dispatchUpdatedPosting();

    this.editPostingUtilsService.addToValidationForms(
      this.descriptionForm.valid,
      EditPostingFormNames.INFO_DESCRIPTION
    );

    if (inputIdentifier === 'trim') {
      this.trimChangeModal.show();
      this.postingAnalyticsHelperService.trackModalView('trimChange');
    }
  }

  restrictFormCharacters() {
    const formFieldValue = this.descriptionForm.get('stockOrAccount').value;
    this.descriptionForm.patchValue({
      stockOrAccount: formFieldValue.replace(/[^a-z0-9]/gi, '')
    });
  }

  fetchVinDecodedData(vin: string): void {
    this.editPostingService
      .getVinDecodeDetails(vin, true)
      .pipe(
        take(1),
        catchError(response => this.handleVinDecodeResponseError())
      )
      .subscribe(vinDecodeRes => {
        this.fetchVinDecodeInProgress = !this.requiredValuesFromVinDecode(vinDecodeRes.vehicleInformation);
        if (vinDecodeRes.vehicleInformation) {
          this.chromeDataInput.manufacturerModelCode = vinDecodeRes?.vehicleInformation?.manufacturerModelCode;
          this.chromeDataInput.manufacturerOptionCodes = vinDecodeRes?.manufacturerOptionCodes;
          this.store.dispatch(new SetChromeDataInput(this.chromeDataInput));
          this.postingData = {
            ...this.postingData,
            inventoryItem: {
              ...this.postingData.inventoryItem,
              vehicleInformation: {
                ...this.postingData.inventoryItem.vehicleInformation,
                make: vinDecodeRes.vehicleInformation.make,
                model: vinDecodeRes.vehicleInformation.model,
                series: vinDecodeRes.vehicleInformation.series,
                vehicleClass: vinDecodeRes.vehicleInformation.vehicleClass,
                modelYear: vinDecodeRes.vehicleInformation.modelYear,
                trim: vinDecodeRes.vehicleInformation.trim
                  ? vinDecodeRes.vehicleInformation.trim
                  : this.postingData.inventoryItem.vehicleInformation.trim
              }
            }
          };
          this.dispatchUpdatedPosting();
          this.triggerVehiclePostingHeaderUpdate();
        }
      });
  }

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly store: Store,
    private readonly translocoService: TranslocoService,
    private readonly editPostingService: EditPostingService,
    private readonly editPostingUtilsService: EditPostingUtilsService,
    private readonly launchDarklyService: LaunchDarklyService,
    private readonly statesProvincesService: StatesProvincesService,
    private readonly postingFormTrackingService: PostingFormTrackingService,
    private readonly inventoryService: InventoryService,
    public readonly postingDataHelperService: PostingDataHelperService,
    private readonly postingAnalyticsHelperService: PostingAnalyticsHelperService
  ) {
    super();
  }

  ngOnInit() {
    this.getTranslations();
    this.initializeStatesProvincesList();
    this.postingType = this.store.selectSnapshot(EditPostingState.postingType);
    this.isRepostBefore60Days = this.postingType === PostingTypes.REPOST_BEFORE_60_DAYS;
    this.descriptionForm = this.formBuilder.group({
      trim: ['default', [descriptionFormDropdownCustomValidator]],
      stockOrAccount: [''],
      vehicleType: ['default', [descriptionFormDropdownCustomValidator]],
      titleState: ['default'],
      odometerMiles: ['', [descriptionFormOdometerCustomValidator]],
      exteriorColor: ['default'],
      interiorType: ['default'],
      interiorColor: ['default'],
      vehicleUse: [this.getVehicleUse()]
    });

    switch (this.postingType) {
      case PostingTypes.REPOST_AFTER_60_DAYS:
      case PostingTypes.REPOST_BEFORE_60_DAYS:
        this.postingNoteParam = DescriptionFormPostingNote.REPOST;
        break;
      case PostingTypes.EDIT:
        this.postingNoteParam = DescriptionFormPostingNote.EDIT;
        break;
      case PostingTypes.NEW:
      default:
        this.postingNoteParam = DescriptionFormPostingNote.NEW;
        break;
    }
    this.originalPostingData = this.store.selectSnapshot(EditPostingState.originalPosting);
    this.store
      .select(EditPostingState.updatedPosting)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(postingData => {
        this.postingData = cloneDeep(postingData);
        this.descriptionForm.patchValue({
          stockOrAccount: _get(this.postingData, 'inventoryItem.postingInformation.stockNumber', ''),
          odometerMiles: _get(this.postingData, `${this.vehicleInfo}.odometer`, ''),
          vehicleUse: this.getVehicleUse()
        });
        this.vehicleTrimOption = _get(this.postingData, 'inventoryItem.vehicleOptions.trimOptions');
        this.exteriorColorOptions = _get(this.postingData, 'inventoryItem.vehicleOptions.exteriorColors');
        this.interiorColorOptions = _get(this.postingData, 'inventoryItem.vehicleOptions.interiorColors');
        this.interiorTypes = _get(this.postingData, 'inventoryItem.vehicleOptions.interiorTypes');
        this.setVehicleTypes(_get(this.postingData, 'inventoryItem.vehicleTypes'));

        this.setTrimOptionsDefaultValue();
        this.setStateOptionsDefaultValue();
        this.setExteriorColorOptionsDefaultValue();
        this.setInteriorColorOptionsDefaultValue();
        this.setInteriorTypeDefaultValue();
        this.setDefaultVehicleType();
        this.chromeDataInput = this.store.selectSnapshot(EditPostingState.chromeDataInput);
        if (this.chromeDataInput === undefined) {
          this.chromeDataInput = {
            manufacturerModelCode: undefined,
            vehicleTrimName: undefined,
            manufacturerOptionCodes: undefined,
            equipmentDescriptions: undefined,
            exteriorColor: undefined,
            interiorColor: undefined
          };
        }
        const trimValue = this.chromeDataInput?.vehicleTrimName;
        if (trimValue === undefined) {
          //if vehicle trim is not set in state then set it from currently selected value from UI
          this.chromeDataInput = { ...this.chromeDataInput, vehicleTrimName: this.descriptionForm.get('trim').value };
          this.store.dispatch(new SetChromeDataInput(this.chromeDataInput));
        }
      });

    //Called for form tracking on reload
    this.postingFormTrackingService.syncFormToInvalidValidationFormsOnLoad(
      this.postingType,
      EditPostingFormNames.INFO_DESCRIPTION
    );
    if (
      this.postingType === PostingTypes.EDIT &&
      !(this.postingAdminEditVehicleTypeEnabled && this.postingData?.inventoryItem?.itemStatus?.status === 'HOLD')
    ) {
      this.descriptionForm.get('vehicleType').disable();
    }
    this.sendPageloadAnalyticsEvent();
  }

  initializeStatesProvincesList(): void {
    this.translocoService
      .selectTranslate('sellModule.posting.descriptionForm.textSelect')
      .pipe(
        filter(text => !!text),
        takeUntil(this.destroyed$)
      )
      .subscribe(textSelect => {
        const defaultOption = [{ name: textSelect, abbr: '--' }];
        this.statesProvincesList = this.canadianTenantEnabled
          ? defaultOption.concat(this.statesProvincesService.getCAProvinces())
          : defaultOption.concat(this.statesProvincesService.getUSStates());
      });
  }

  sendPageloadAnalyticsEvent() {
    this.postingAnalyticsHelperService.sendPageloadAnalyticsEvent(
      this.store.selectSnapshot(ConfigurationState.configuration).tenantName,
      this.postingTypeString,
      EditPostingFormNames.INFO_DESCRIPTION
    );
  }

  setVehicleTypes(vehicleTypes: VehicleType[]) {
    if (vehicleTypes?.length > 0) {
      this.vehicleTypes = this.sortVehicleTypesByDescriptionAndCode(vehicleTypes);
    } else {
      this.vehicleTypes = [];
    }
  }

  sortVehicleTypesByDescriptionAndCode(vehicleTypes): VehicleType[] {
    const english = 'en';
    this.sanitizeNullAndUndefinedValues(vehicleTypes);

    //Below method sorts by typeDescription, if descriptions are equal then sorts by typeCode
    return vehicleTypes.sort(
      (firstVehicleType, secondVehicleType) =>
        firstVehicleType.typeDescription.localeCompare(secondVehicleType.typeDescription, english) ||
        firstVehicleType.typeCode.localeCompare(secondVehicleType.typeCode, english)
    );
  }

  private sanitizeNullAndUndefinedValues(vehicleTypes: VehicleType[]): void {
    const emptyString = '';
    vehicleTypes.forEach(vehicleType => {
      if (this.isNullOrUndefined(vehicleType.typeDescription)) {
        vehicleType.typeDescription = emptyString;
      }

      if (this.isNullOrUndefined(vehicleType.typeCode)) {
        vehicleType.typeCode = emptyString;
      }
    });
  }

  isNullOrUndefined(value) {
    return value === undefined || value === null;
  }

  getVehicleUse() {
    if (_get(this.postingData, `${this.vehicleInfo}.commercialVehicle`)) {
      return 'Commercial';
    } else if (_get(this.postingData, `${this.vehicleInfo}.recreationalVehicle`)) {
      return 'Recreational';
    } else {
      return 'Neither';
    }
  }

  setDefaultVehicleType() {
    this.descriptionForm.patchValue({
      vehicleType: this.vehicleTypes?.filter(e => e.selected)[0]
        ? this.vehicleTypes.filter(e => e.selected)[0].typeCode
        : 'default'
    });
  }

  setTrimOptionsDefaultValue() {
    this.descriptionForm.patchValue({
      trim: this.vehicleTrimOption?.filter(e => e.selected)[0]
        ? this.vehicleTrimOption.filter(e => e.selected)[0].vehicleTrimName
        : 'default'
    });
  }

  setExteriorColorOptionsDefaultValue() {
    this.descriptionForm.patchValue({
      exteriorColor: this.exteriorColorOptions?.filter(e => e.installed)[0]
        ? this.exteriorColorOptions.filter(e => e.installed)[0].colorCode
        : 'default'
    });
  }

  setInteriorColorOptionsDefaultValue() {
    this.descriptionForm.patchValue({
      interiorColor: this.interiorColorOptions?.filter(e => e.installed)[0]
        ? this.interiorColorOptions.filter(e => e.installed)[0].colorCode
        : 'default'
    });
  }

  setInteriorTypeDefaultValue() {
    this.descriptionForm.patchValue({
      interiorType: this.interiorTypes?.filter(e => e.installed)[0]
        ? this.interiorTypes.filter(e => e.installed)[0].optionCode
        : 'default'
    });
  }

  setStateOptionsDefaultValue() {
    this.descriptionForm.patchValue({
      titleState: this.postingData.inventoryItem.titleState?.stateCode || '0: --'
    });
  }

  isTrimDisabled() {
    if (this.vehicleTrimOption === undefined || this.vehicleTrimOption.length === 0) {
      this.descriptionForm.controls.trim.removeValidators(Validators.required);
      this.descriptionForm.controls.trim.disable();
      this.trimPlaceholder = 'Unavailable';
      return true;
    } else {
      return false;
    }
  }

  showTrimError() {
    return this.descriptionForm.get('trim').value === 'default';
  }

  showVehicleTypeError() {
    return this.descriptionForm.get('vehicleType').value === 'default';
  }

  get vehicleType() {
    return this.descriptionForm.get('vehicleType');
  }

  get odometerMiles() {
    return this.descriptionForm.get('odometerMiles').value;
  }

  get isOdometerValid() {
    return this.odometerMiles && this.odometerMiles > this.defaultOdometer;
  }

  getTranslations() {
    const keyParams = {
      common: {},
      'sellModule.posting.descriptionForm': {},
      search: {}
    };

    this.translocoService
      .selectTranslateObject(keyParams)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        translate => {
          this.descriptionFormTranslations.color = translate[0].color;
          this.trimPlaceholder = translate[1].textSelect;
          this.vinDecodeErrorMessage = translate[1].vinDecodeErrorMessage;
          this.descriptionFormTranslations.labelExteriorInterior = translate[1].labelExteriorInterior;
          this.descriptionFormTranslations.labelExterior = translate[2].inventoryItem.labelExterior;
          this.descriptionFormTranslations.labelInterior = translate[2].inventoryItem.labelInterior;
          this.descriptionFormTranslations.labelOdometer = this.canadianTenantEnabled
            ? translate[1].labelOdometerKilometers
            : translate[1].labelOdometerMiles;
          this.descriptionFormTranslations.labelTitleStateOrProvince = this.canadianTenantEnabled
            ? translate[1].labelTitleProvince
            : translate[1].labelTitleState;
        },
        error => {
          console.log(error);
        }
      );
  }

  dispatchUpdatedPosting() {
    this.store.dispatch(new UpdatePosting(this.postingData));
  }

  requiredValuesFromVinDecode(vehicleInformation: VehicleInformation): boolean {
    if (
      vehicleInformation &&
      vehicleInformation.make &&
      vehicleInformation.model &&
      vehicleInformation.modelYear &&
      vehicleInformation.series &&
      vehicleInformation.vehicleClass
    ) {
      return true;
    } else {
      return false;
    }
  }

  requiredValuesRetrievedFromChrome(response: InspectionOptionResponseModel): boolean {
    if (response.vehicleOptions) {
      this.trimChangeModal.close();
      return true;
    } else {
      return false;
    }
  }

  handleVinDecodeResponseError() {
    this.fetchVinDecodeInProgress = false;
    this.errorModal.message = this.vinDecodeErrorMessage;
    this.errorModal.show();
    return of();
  }

  triggerVehiclePostingHeaderUpdate(): void {
    this.vehicleHeaderHelper.emit();
  }

  get showBuyNowNotAllowedError(): boolean {
    // When default/'Select' is selected in the UI, the vehicle types will not have a selection.
    const selectedOption: VehicleType = this.vehicleTypes?.find(vh => vh.selected);
    if (selectedOption) {
      return !selectedOption?.buyNowAllowed;
    } else {
      return false;
    }
  }

  get canadianTenantEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('canadianTenantEnabled', false);
  }

  continueWithChromeCall() {
    this.chromeResponseInProgress = true;
    let chromeDataFromState = this.store.selectSnapshot(EditPostingState.chromeDataInput);
    chromeDataFromState = { ...chromeDataFromState, vehicleTrimName: this.descriptionForm.get('trim').value };
    this.store.dispatch(new SetChromeDataInput(chromeDataFromState));
    this.setTrimOptionsDefaultValue();
    this.getChromeVehicleData(this.store.selectSnapshot(EditPostingState.selectedInspectionOption));
  }

  cancel() {
    //revert to previously selected trim value on click of cancel button in trim modal
    const chromeDataFromState = this.store.selectSnapshot(EditPostingState.chromeDataInput);
    this.descriptionForm.patchValue({
      trim: chromeDataFromState?.vehicleTrimName
    });
    _set(_find(this.vehicleTrimOption, { selected: true }), 'selected', false);
    _set(_find(this.vehicleTrimOption, { vehicleTrimName: this.descriptionForm.get('trim').value }), 'selected', true);
    this.dispatchUpdatedPosting();
    this.trimChangeModal.close();
  }

  getChromeVehicleData(selectedInspectionOption: NewPostingInspectionOption) {
    const vin = this.postingData.inventoryItem.vehicleInformation.vin;
    let postingErrorResponse;
    const trimOptionsList = this.postingData.inventoryItem.vehicleOptions?.trimOptions;
    let chromeOptionsRequest;
    if (this.postingData.inventoryItem.postingInformation.thirdPartyPosting) {
      chromeOptionsRequest = this.postingDataHelperService.buildThirdPartyChromeOptionsRequest();
    } else {
      chromeOptionsRequest = this.postingDataHelperService.buildChromeOptionsRequest(
        selectedInspectionOption === NewPostingInspectionOption.USE_EXISTING
      );
    }
    this.inventoryService
      .getInspectionData(vin, chromeOptionsRequest)
      .pipe(take(1))
      .subscribe(
        response => {
          this.chromeOptions = response?.vehicleOptions;
          this.postingData.inventoryItem.vehicleOptions = this.chromeOptions;
          //original trim options are overriden if selected trim is used for options call and hence preserving
          // original list so that all possible trim options are displayed in UI everytime
          this.postingData.inventoryItem.vehicleOptions.trimOptions = trimOptionsList;
          this.dispatchUpdatedPosting();
          this.chromeResponseInProgress = !this.requiredValuesRetrievedFromChrome(response);
        },
        error => {
          postingErrorResponse = this.postingDataHelperService.handleErrorMessaging(error);
          this.errorHeaderKey = postingErrorResponse.errorHeaderKey;
          this.errorMessageKey = postingErrorResponse.errorMessageKey;
          this.trimChangeModal.close();
        }
      );
  }

  get postingAdminEditVehicleTypeEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('posting-adminEditVehicleTypeEnabled', false);
  }
}
